<template>
  <div :class="['settings-page', `theme-${currentTheme}`]">
    <!-- 页面标题 -->
    <h2 class="page-title">系统基本设置</h2>

    <!-- 设置表单卡片 -->
    <Card class="settings-card">
      <Form ref="settingsForm" :model="siteInfo" :rules="rules" label-position="left" :label-width="120">
        
        <!-- 网站名称 -->
        <FormItem label="网站名称" prop="site_name">
          <Input v-model="siteInfo.site_name" placeholder="请输入网站名称" />
        </FormItem>

        <!-- 网站描述 -->
        <FormItem label="网站描述" prop="site_description">
          <Input 
            v-model="siteInfo.site_description" 
            type="textarea" 
            :rows="4"
            placeholder="请输入网站描述" 
          />
        </FormItem>

        <!-- 网站关键词 -->
        <FormItem label="网站关键词" prop="site_keywords">
          <Input v-model="siteInfo.site_keywords" placeholder="请输入网站关键词，多个关键词用逗号分隔" />
        </FormItem>

        <!-- 允许积分充值 -->
        <FormItem label="允许积分充值">
          <iSwitch v-model="siteInfo.allow_point_recharge" size="large">
            <span slot="open">开启</span>
            <span slot="close">关闭</span>
          </iSwitch>
        </FormItem>

        <!-- 联系邮箱 -->
        <FormItem label="联系邮箱" prop="contact_email">
          <Input v-model="siteInfo.contact_email" placeholder="请输入联系邮箱" />
        </FormItem>

        <!-- 客服电话 -->
        <FormItem label="客服电话" prop="contact_phone">
          <Input v-model="siteInfo.contact_phone" placeholder="请输入客服电话" />
        </FormItem>

        <!-- 操作按钮 -->
        <FormItem>
          <Button type="primary" :loading="saving" @click="saveSettings" class="btn-save">
            <Icon type="ios-checkmark-circle" class="btn-icon"/>
            保存设置
          </Button>
          <Button @click="resetSettings" class="btn-reset">
            <Icon type="ios-refresh" class="btn-icon"/>
            重置
          </Button>
        </FormItem>
      </Form>
    </Card>
  </div>
</template>

<script>
import apiClient from '@/api/index';

export default {
  name: 'SystemSettings',
  data() {
    return {
      currentTheme: 'starry',
      siteInfo: {
        site_name: '',
        site_description: '',
        site_keywords: '',
        allow_point_recharge: false,
        contact_email: '',
        contact_phone: ''
      },
      originalSiteInfo: null, // 用于重置
      saving: false,
      rules: {
        site_name: [
          { required: true, message: '请输入网站名称', trigger: 'blur' }
        ],
        contact_email: [
          { type: 'email', message: '请输入有效的邮箱地址', trigger: 'blur' }
        ]
      }
    };
  },
  created() {
    this.fetchSiteInfo();
    this.loadTheme();
  },
  mounted() {
    // 检查登录状态
    const isUserLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
    if (isUserLoggedIn) {
      document.title = '抖数君-系统基本设置';
    } else {
      document.title = '系统基本设置 - 抖数君平台';
    }
    window.addEventListener('theme-changed', this.onThemeChanged);
  },
  beforeDestroy() {
    window.removeEventListener('theme-changed', this.onThemeChanged);
  },
  methods: {
    // 加载当前主题
    loadTheme() {
      const savedTheme = localStorage.getItem('currentTheme') || 'starry';
      this.currentTheme = savedTheme;
    },
    // 响应全局主题切换事件
    onThemeChanged(event) {
      const themeName = event && event.detail ? event.detail.theme : event;
      if (themeName) {
        this.currentTheme = themeName;
      }
    },

    // 获取网站信息
    async fetchSiteInfo() {
      try {
        const response = await apiClient.get('admin/getsite');
        if (response.data.code === 200) {
          this.siteInfo = response.data.data;
          // 保存原始数据用于重置
          this.originalSiteInfo = JSON.parse(JSON.stringify(this.siteInfo));
        } else {
          this.$Message.error(response.data.msg || '获取网站信息失败');
        }
      } catch (error) {
        console.error('获取网站信息失败:', error);
        this.$Message.error('获取网站信息失败，请稍后重试');
      }
    },

    // 保存设置
    async saveSettings() {
      try {
        // 验证表单
        this.$refs.settingsForm.validate(async (valid) => {
          if (valid) {
            this.saving = true;
            
            const response = await apiClient.post('admin/updatesite', this.siteInfo);
            
            if (response.data.code === 200) {
              this.$Message.success('设置保存成功');
              // 更新原始数据
              this.originalSiteInfo = JSON.parse(JSON.stringify(this.siteInfo));
              
              // 如果修改了网站名称，更新页面标题
              if (this.siteInfo.site_name) {
                document.title = this.siteInfo.site_name;
              }
            } else {
              this.$Message.error(response.data.msg || '保存设置失败');
            }
          } else {
            this.$Message.warning('请检查表单填写是否正确');
          }
        });
      } catch (error) {
        console.error('保存设置失败:', error);
        this.$Message.error('保存设置失败，请稍后重试');
      } finally {
        this.saving = false;
      }
    },

    // 重置设置
    resetSettings() {
      if (this.originalSiteInfo) {
        this.siteInfo = JSON.parse(JSON.stringify(this.originalSiteInfo));
        this.$Message.info('已重置为上次保存的设置');
      } else {
        this.$Message.warning('没有可重置的数据');
      }
    }
  }
};
</script>

<style scoped>
/* 页面容器样式 */
.settings-page {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
  transition: background 0.3s ease;
}

/* 页面标题样式 */
.page-title {
  text-align: center;
  font-size: 26px;
  font-weight: 600;
  margin-bottom: 32px;
  color: #333;
  letter-spacing: 1px;
}

/* 设置卡片样式 */
.settings-card {
  max-width: 800px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
}

.settings-card >>> .ivu-card-body {
  padding: 28px 32px;
  background: transparent;
}

/* 表单样式 */
.settings-card >>> .ivu-form-item {
  margin-bottom: 22px;
}

.settings-card >>> .ivu-form-item-label {
  font-weight: 500;
  color: #515a6e;
  font-size: 14px;
}

/* 输入框样式优化 */
.settings-card >>> .ivu-input,
.settings-card >>> .ivu-textarea-inner {
  border-radius: 8px;
  transition: all 0.3s ease;
  height: 40px;
  font-size: 14px;
}

.settings-card >>> .ivu-input:focus,
.settings-card >>> .ivu-textarea-inner:focus {
  border-color: #2d8cf0;
  box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.15);
}

/* 开关样式优化 */
.settings-card >>> .ivu-switch {
  vertical-align: middle;
  width: 56px;
  height: 28px;
}

.settings-card >>> .ivu-switch::after {
  width: 24px;
  height: 24px;
  top: 1px;
  left: 1px;
}

.settings-card >>> .ivu-switch-checked::after {
  left: 30px;
}

/* 按钮样式优化 */
.settings-card >>> .ivu-btn {
  border-radius: 8px;
  padding: 0 24px;
  height: 40px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon {
  margin-right: 6px;
  font-size: 16px;
}

.settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #2d8cf0 0%, #5cadff 100%) !important;
  border: none !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.3);
}

.settings-card >>> .ivu-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(45, 140, 240, 0.4);
}

.settings-card >>> .ivu-btn-primary:disabled {
  background: #c5c8ce !important;
  transform: none;
  box-shadow: none;
  color: #fff !important;
}

.settings-card >>> .ivu-btn-default {
  background: transparent !important;
  border: 1px solid #2d8cf0 !important;
  color: #2d8cf0 !important;
  margin-left: 12px;
}

.settings-card >>> .ivu-btn-default:hover {
  background: #ecf5ff !important;
  color: #2d8cf0 !important;
  transform: translateY(-2px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .settings-page {
    padding: 16px;
  }

  .page-title {
    font-size: 22px;
    margin-bottom: 24px;
  }

  .settings-card >>> .ivu-card-body {
    padding: 20px;
  }

  .settings-card >>> .ivu-form-item-label {
    width: 100px !important;
  }

  .settings-card >>> .ivu-btn {
    width: 100%;
    margin-left: 0 !important;
  }

  .settings-card >>> .ivu-form-item-content {
    display: flex;
    flex-direction: column;
  }
}

/* ==================== 经典主题 ==================== */
.theme-classic .settings-page {
  background-color: #f5f7fa !important;
}

.theme-classic .page-title {
  color: #333 !important;
}

.theme-classic .settings-card {
  background: #ffffff !important;
}

.theme-classic .settings-card >>> .ivu-form-item-label {
  color: #515a6e !important;
}

.theme-classic .settings-card >>> .ivu-input,
.theme-classic .settings-card >>> .ivu-textarea-inner {
  background: #ffffff !important;
  color: #333 !important;
  border-color: #e8e8e8 !important;
}

.theme-classic .settings-card >>> .ivu-input::placeholder,
.theme-classic .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #999 !important;
}

.theme-classic .settings-card >>> .ivu-input:focus,
.theme-classic .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #2d8cf0 !important;
  box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.15) !important;
}

.theme-classic .settings-card >>> .ivu-switch-checked {
  background: #2d8cf0 !important;
  border-color: #2d8cf0 !important;
}

/* ==================== 星空主题（与主页保持一致）==================== */
.theme-starry .settings-page {
  background: linear-gradient(135deg, #0c1445 0%, #1a237e 50%, #0d47a1 100%) !important;
}

.theme-starry .page-title {
  color: #e2e8f0 !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.theme-starry .settings-card {
  background: rgba(22, 33, 62, 0.92) !important;
  border: 1px solid rgba(100, 255, 218, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35) !important;
  backdrop-filter: blur(10px);
}

.theme-starry .settings-card >>> .ivu-form-item-label {
  color: #e2e8f0 !important;
}

.theme-starry .settings-card >>> .ivu-input,
.theme-starry .settings-card >>> .ivu-textarea-inner {
  background: #0f0f1a !important;
  color: #e2e8f0 !important;
  border-color: #2a2a3e !important;
}

.theme-starry .settings-card >>> .ivu-input::placeholder,
.theme-starry .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #8892b0 !important;
  opacity: 0.8;
}

.theme-starry .settings-card >>> .ivu-input:focus,
.theme-starry .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #64ffda !important;
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15) !important;
}

.theme-starry .settings-card >>> .ivu-switch-checked {
  background: #64ffda !important;
  border-color: #64ffda !important;
}

.theme-starry .settings-card >>> .ivu-switch-checked .ivu-switch-inner {
  color: #0a0e27 !important;
}

.theme-starry .settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #64ffda 0%, #00bcd4 100%) !important;
  color: #0a0e27 !important;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.3) !important;
}

.theme-starry .settings-card >>> .ivu-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(100, 255, 218, 0.4) !important;
}

.theme-starry .settings-card >>> .ivu-btn-primary:disabled {
  background: #2a2a4e !important;
  color: #8892b0 !important;
  box-shadow: none !important;
}

.theme-starry .settings-card >>> .ivu-btn-default {
  border-color: #64ffda !important;
  color: #64ffda !important;
  background: transparent !important;
}

.theme-starry .settings-card >>> .ivu-btn-default:hover {
  background: #1a2745 !important;
  color: #64ffda !important;
}

/* ==================== 蓝色主题 ==================== */
.theme-blue .settings-page {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
}

.theme-blue .page-title {
  color: #1565C0 !important;
}

.theme-blue .settings-card {
  background: #ffffff !important;
}

.theme-blue .settings-card >>> .ivu-form-item-label {
  color: #1565C0 !important;
}

.theme-blue .settings-card >>> .ivu-input,
.theme-blue .settings-card >>> .ivu-textarea-inner {
  background: #ffffff !important;
  color: #1565C0 !important;
  border-color: #90CAF9 !important;
}

.theme-blue .settings-card >>> .ivu-input::placeholder,
.theme-blue .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #90CAF9 !important;
}

.theme-blue .settings-card >>> .ivu-input:focus,
.theme-blue .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #0D47A1 !important;
  box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.15) !important;
}

.theme-blue .settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #1976D2 0%, #42A5F5 100%) !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
}

.theme-blue .settings-card >>> .ivu-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(25, 118, 210, 0.4) !important;
}

.theme-blue .settings-card >>> .ivu-btn-default {
  border-color: #1976D2 !important;
  color: #1976D2 !important;
}

.theme-blue .settings-card >>> .ivu-btn-default:hover {
  background: #E3F2FD !important;
}

.theme-blue .settings-card >>> .ivu-switch-checked {
  background: #1976D2 !important;
  border-color: #1976D2 !important;
}

/* ==================== 紫色主题 ==================== */
.theme-purple .settings-page {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%) !important;
}

.theme-purple .page-title {
  color: #6A1B9A !important;
}

.theme-purple .settings-card {
  background: #ffffff !important;
}

.theme-purple .settings-card >>> .ivu-form-item-label {
  color: #6A1B9A !important;
}

.theme-purple .settings-card >>> .ivu-input,
.theme-purple .settings-card >>> .ivu-textarea-inner {
  background: #ffffff !important;
  color: #6A1B9A !important;
  border-color: #CE93D8 !important;
}

.theme-purple .settings-card >>> .ivu-input::placeholder,
.theme-purple .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #CE93D8 !important;
}

.theme-purple .settings-card >>> .ivu-input:focus,
.theme-purple .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #4A148C !important;
  box-shadow: 0 0 0 3px rgba(74, 20, 140, 0.15) !important;
}

.theme-purple .settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #7B1FA2 0%, #AB47BC 100%) !important;
  box-shadow: 0 4px 12px rgba(123, 31, 162, 0.3) !important;
}

.theme-purple .settings-card >>> .ivu-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(123, 31, 162, 0.4) !important;
}

.theme-purple .settings-card >>> .ivu-btn-default {
  border-color: #7B1FA2 !important;
  color: #7B1FA2 !important;
}

.theme-purple .settings-card >>> .ivu-btn-default:hover {
  background: #F3E5F5 !important;
}

.theme-purple .settings-card >>> .ivu-switch-checked {
  background: #7B1FA2 !important;
  border-color: #7B1FA2 !important;
}

/* ==================== 绿色主题 ==================== */
.theme-green .settings-page {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
}

.theme-green .page-title {
  color: #2E7D32 !important;
}

.theme-green .settings-card {
  background: #ffffff !important;
}

.theme-green .settings-card >>> .ivu-form-item-label {
  color: #2E7D32 !important;
}

.theme-green .settings-card >>> .ivu-input,
.theme-green .settings-card >>> .ivu-textarea-inner {
  background: #ffffff !important;
  color: #2E7D32 !important;
  border-color: #81C784 !important;
}

.theme-green .settings-card >>> .ivu-input::placeholder,
.theme-green .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #81C784 !important;
}

.theme-green .settings-card >>> .ivu-input:focus,
.theme-green .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #1B5E20 !important;
  box-shadow: 0 0 0 3px rgba(27, 94, 32, 0.15) !important;
}

.theme-green .settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #388E3C 0%, #66BB6A 100%) !important;
  box-shadow: 0 4px 12px rgba(56, 142, 60, 0.3) !important;
}

.theme-green .settings-card >>> .ivu-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(56, 142, 60, 0.4) !important;
}

.theme-green .settings-card >>> .ivu-btn-default {
  border-color: #388E3C !important;
  color: #388E3C !important;
}

.theme-green .settings-card >>> .ivu-btn-default:hover {
  background: #E8F5E9 !important;
}

.theme-green .settings-card >>> .ivu-switch-checked {
  background: #388E3C !important;
  border-color: #388E3C !important;
}

/* ==================== 红色主题 ==================== */
.theme-red .settings-page {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%) !important;
}

.theme-red .page-title {
  color: #C62828 !important;
}

.theme-red .settings-card {
  background: #ffffff !important;
}

.theme-red .settings-card >>> .ivu-form-item-label {
  color: #C62828 !important;
}

.theme-red .settings-card >>> .ivu-input,
.theme-red .settings-card >>> .ivu-textarea-inner {
  background: #ffffff !important;
  color: #C62828 !important;
  border-color: #EF9A9A !important;
}

.theme-red .settings-card >>> .ivu-input::placeholder,
.theme-red .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #EF9A9A !important;
}

.theme-red .settings-card >>> .ivu-input:focus,
.theme-red .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #B71C1C !important;
  box-shadow: 0 0 0 3px rgba(183, 28, 28, 0.15) !important;
}

.theme-red .settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #D32F2F 0%, #EF5350 100%) !important;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3) !important;
}

.theme-red .settings-card >>> .ivu-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(211, 47, 47, 0.4) !important;
}

.theme-red .settings-card >>> .ivu-btn-default {
  border-color: #D32F2F !important;
  color: #D32F2F !important;
}

.theme-red .settings-card >>> .ivu-btn-default:hover {
  background: #FFEBEE !important;
}

.theme-red .settings-card >>> .ivu-switch-checked {
  background: #D32F2F !important;
  border-color: #D32F2F !important;
}

/* ==================== 橙色主题 ==================== */
.theme-orange .settings-page {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%) !important;
}

.theme-orange .page-title {
  color: #E65100 !important;
}

.theme-orange .settings-card {
  background: #ffffff !important;
}

.theme-orange .settings-card >>> .ivu-form-item-label {
  color: #E65100 !important;
}

.theme-orange .settings-card >>> .ivu-input,
.theme-orange .settings-card >>> .ivu-textarea-inner {
  background: #ffffff !important;
  color: #E65100 !important;
  border-color: #FFB74D !important;
}

.theme-orange .settings-card >>> .ivu-input::placeholder,
.theme-orange .settings-card >>> .ivu-textarea-inner::placeholder {
  color: #FFB74D !important;
}

.theme-orange .settings-card >>> .ivu-input:focus,
.theme-orange .settings-card >>> .ivu-textarea-inner:focus {
  border-color: #BF360C !important;
  box-shadow: 0 0 0 3px rgba(191, 54, 12, 0.15) !important;
}

.theme-orange .settings-card >>> .ivu-btn-primary {
  background: linear-gradient(135deg, #F57C00 0%, #FFA726 100%) !important;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.3) !important;
}

.theme-orange .settings-card >>> .ivu-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(245, 124, 0, 0.4) !important;
}

.theme-orange .settings-card >>> .ivu-btn-default {
  border-color: #F57C00 !important;
  color: #F57C00 !important;
}

.theme-orange .settings-card >>> .ivu-btn-default:hover {
  background: #FFF3E0 !important;
}

.theme-orange .settings-card >>> .ivu-switch-checked {
  background: #F57C00 !important;
  border-color: #F57C00 !important;
}</style>
